"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight } from "@/components/icons";
import { buttonVariants } from "@/components/ui/button";
import { ROUTES } from "@/lib/constants";
import { ComparisonTable } from "./parts";

const HEADERS = ["What you need", "Usually appropriate", "Proxidize option"];

const ROWS = [
  [
    "Global public-web collection and monitoring",
    "Rotating or sticky residential proxies",
    "Residential Proxies",
  ],
  ["Mobile-network context in supported locations", "Mobile proxy pool", "Mobile Per GB"],
  ["An individually assigned mobile route", "Real mobile access", "Mobile Per Proxy"],
  [
    "One fixed allowlisted IP for a long period",
    "Static, dedicated, or ISP proxy",
    "Choose an available static product; do not force a rotating service into this use case",
  ],
  [
    "A small job that already works directly",
    "No proxy may be necessary",
    "Start with the simplest route that works",
  ],
];

export default function ProxySelector() {
  const reduced = useReducedMotion() ?? false;

  return (
    <section
      aria-labelledby="proxy-selector-heading"
      className="border-border py-section-sm relative border-b"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5 }}
        >
          <p className="text-muted-foreground mb-3 text-xs font-semibold">Decision guide</p>
          <h2
            id="proxy-selector-heading"
            className="text-foreground mb-3 scroll-mt-40 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            Which proxy type do you need?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-2xl text-base leading-relaxed">
            Match your workload to a proxy type before comparing providers or pricing.
          </p>

          <ComparisonTable headers={HEADERS} rows={ROWS} />

          <div className="mt-2 flex flex-wrap gap-3">
            <Link
              href={ROUTES.productResidentialProxies}
              className={buttonVariants({ variant: "default", size: "lg" })}
            >
              Explore Residential Proxies
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              href={ROUTES.productMobileProxies}
              className={buttonVariants({ variant: "outline", size: "lg" })}
            >
              Explore Mobile Proxies
            </Link>
            <Link
              href={ROUTES.pricing}
              className={buttonVariants({ variant: "outline", size: "lg" })}
            >
              Compare Pricing
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
